import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../core/types/api.types';

/**
 * Source d'une feature résolue (DEFAULT / PLAN:{id} / USER_TYPE:{type} / USER:{id}).
 */
export interface ResolvedFeature {
  label: string;
  type: 'FLAG' | 'QUOTA';
  audience: string;
  enabled: boolean;
  intValue: number;
  source: string;
}

/**
 * Service client-side pour interroger les features actives pour l'utilisateur
 * courant. Charge le snapshot après login et expose isEnabled / quotaFor.
 */
@Injectable({ providedIn: 'root' })
export class FeatureService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/admin`;
  private root = environment.apiUrl;

  /** true une fois le snapshot de l'utilisateur courant récupéré du backend. */
  private loaded = false;

  /** Map { FEATURE_KEY → ResolvedFeature }. Vide tant que non chargé. */
  private features$ = new BehaviorSubject<Record<string, ResolvedFeature>>({});

  /** Charge les features pour un user donné (admin uniquement). */
  loadForUser(userReference: string): Observable<Record<string, ResolvedFeature>> {
    return this.http
      .get<ApiResponse<Record<string, ResolvedFeature>>>(
        `${this.base}/users/${userReference}/features/resolved`,
      )
      .pipe(
        map((r) => r.data || {}),
        tap((data) => this.features$.next(data)),
      );
  }

  /**
   * Charge le snapshot des features de l'utilisateur COURANT via l'endpoint
   * self {@code GET /me/features}. À appeler après login et au démarrage.
   */
  loadForCurrentUser(): Observable<Record<string, ResolvedFeature>> {
    return this.http
      .get<ApiResponse<Record<string, ResolvedFeature>>>(`${this.root}/me/features`)
      .pipe(
        map((r) => r.data || {}),
        tap((data) => {
          this.features$.next(data);
          this.loaded = true;
        }),
      );
  }

  /**
   * Retourne le snapshot courant, en le chargeant depuis le backend s'il ne
   * l'a pas encore été. Utilisé par le {@code featureGuard} pour éviter toute
   * course entre la navigation et le chargement des features.
   */
  ensureLoaded(): Observable<Record<string, ResolvedFeature>> {
    return this.loaded
      ? of(this.features$.value)
      : this.loadForCurrentUser();
  }

  /** Observable du snapshot courant. */
  watch(): Observable<Record<string, ResolvedFeature>> {
    return this.features$.asObservable();
  }

  /** {@code true} si la feature est activée. */
  isEnabled(key: string): boolean {
    const v = this.features$.value[key];
    return !!v && v.enabled;
  }

  /** Limite numérique pour cette feature. {@code -1} = illimité. */
  quotaFor(key: string): number {
    const v = this.features$.value[key];
    return v ? v.intValue : 0;
  }

  /** Réinitialise (au logout par exemple). */
  clear(): void {
    this.features$.next({});
    this.loaded = false;
  }
}
